import { useStorage } from '@hooks'
import { voteSurvey } from '@lib/firebase/api'
import { ISurvey } from '@models'
import { sendGAEvent } from '@next/third-parties/google'
import { useCallback, useEffect, useState } from 'react'

const VOTE_COOLDOWN = 30 * 1000 // 30 seconds in milliseconds

export default function useSurveys(stationId: string, initial: ISurvey[]) {
    const { get, set, update } = useStorage('surveys')
    const [surveys, setSurveys] = useState<ISurvey[]>(initial || [])
    const [showing, setShowing] = useState(0)
    const [voted, setVoted] = useState<number>(-1)
    const [loading, setLoading] = useState(true)

    const updateSurveys = useCallback(async () => {
        const history: { id: string; voted: number; timestamp: number }[] = get() || []
        const historyMap = new Map(history.map(h => [h.id, { voted: h.voted, timestamp: h.timestamp }]))

        const newSurveys = surveys.map(s => {
            const totalVotes = s.answers.reduce((acc, answer) => acc + answer.votes, 0)
            s.answers.forEach(a => a.percentage = totalVotes ? Math.round((a.votes / totalVotes) * 100) : 0)
            return s
        })

        setSurveys(newSurveys)
        const voteData = historyMap.get(newSurveys[showing]?.id)
        setVoted(voteData ? voteData.voted : -1)
        setLoading(false)
    }, [showing, surveys])

    const nextSurvey = useCallback(() => {
        const next = (showing + 1) % surveys.length
        setShowing(next)
    }, [surveys, showing, get])

    const vote = useCallback(async (newVote: number) => {
        if (voted === newVote || loading) return // Don't re-vote the same answer

        const surveyId = surveys[showing]?.id
        if (!surveyId) return

        // Get last vote timestamp
        const history: { id: string, voted: number, timestamp: number }[] = get() || []
        const lastVoteData  = history.find(h => h.id === surveyId)
        const lastVoteTime = lastVoteData ? lastVoteData.timestamp : 0
        const now = Date.now()

        // Check if cooldown time has passed
        if (lastVoteTime && now - lastVoteTime < VOTE_COOLDOWN) {
            alert(`❌ יש להמתין ${Math.ceil((VOTE_COOLDOWN - (now - lastVoteTime)) / 1000)} שניות לפני שינוי הצבעה.`)
            return
        }

        setLoading(true)
        
        await voteSurvey(surveyId, newVote, voted)
        
        sendGAEvent('event', `survey-${surveyId}`, { screen_name: stationId, vote: newVote })

        // Update local votes without refetching
        setSurveys(prevSurveys =>
            prevSurveys.map(s => {
                if (s.id !== surveyId) return s

                const updatedAnswers = s.answers.map((a, index) => ({
                    ...a,
                    votes: index === newVote ? a.votes + 1 : index === voted ? a.votes - 1 : a.votes
                }))

                const totalVotes = updatedAnswers.reduce((acc, answer) => acc + answer.votes, 0)
                updatedAnswers.forEach(a => a.percentage = totalVotes ? Math.round((a.votes / totalVotes) * 100) : 0)

                return { ...s, answers: updatedAnswers }
            })
        )

        // Save vote history with timestamp
        update(surveyId, { id: surveyId, voted: newVote, timestamp: now })
        setVoted(newVote)
        setLoading(false)
    }, [voted, surveys, showing, loading, update, stationId, get])

    useEffect(() => {
        updateSurveys()
    }, [surveys, showing])

    return { surveys, showing, vote, nextSurvey, voted, loading }
}
